import React, { useEffect, useRef, useState } from 'react';
import JsonDiff from './JsonDiff';

const MessagesContainer = ({ messages, intents, onMessageClick, conversationStatus, selectedMessageId }) => {
  const messagesEndRef = useRef(null);
  const containerRef = useRef(null);
  const messageRefs = useRef({});
  const [expandedCartDiff, setExpandedCartDiff] = useState(null);

  const parseRawPayload = (rawPayload) => {
    if (!rawPayload) return null;

    if (typeof rawPayload === 'string') {
      try {
        return JSON.parse(rawPayload);
      } catch (_) {
        return null;
      }
    }

    return rawPayload;
  };

  const getToolCallNames = (message) => {
    const payload = parseRawPayload(message?.raw_payload);
    const output = payload?.vapi?.output;
    if (!Array.isArray(output)) return [];

    const names = [];
    const seen = new Set();

    output.forEach((msg) => {
      if (!msg || typeof msg !== 'object') return;
      if (msg.role !== 'assistant' || !Array.isArray(msg.tool_calls)) return;

      msg.tool_calls.forEach((call) => {
        const name = call?.function?.name;
        if (!name || seen.has(name)) return;
        seen.add(name);
        names.push(name);
      });
    });

    return names;
  };

  const getToolSummary = (message) => {
    const names = getToolCallNames(message);
    if (names.length === 0) return null;

    const preview = names.slice(0, 2);
    const suffix = names.length > 2 ? ` +${names.length - 2} more` : '';

    return {
      count: names.length,
      full: names.join(', '),
      display: `${preview.join(', ')}${suffix}`
    };
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const generateJsonDiff = (oldObj, newObj) => {
    const oldJson = JSON.stringify(oldObj, null, 2).split('\n');
    const newJson = JSON.stringify(newObj, null, 2).split('\n');
    
    // Use a more sophisticated diff algorithm that groups changes
    const diff = [];
    let i = 0, j = 0;
    
    while (i < oldJson.length || j < newJson.length) {
      if (i >= oldJson.length) {
        // Rest of new lines
        const addedLines = [];
        while (j < newJson.length) {
          addedLines.push(newJson[j++]);
        }
        if (addedLines.length > 0) {
          diff.push({ type: 'added', lines: addedLines });
        }
        break;
      }
      
      if (j >= newJson.length) {
        // Rest of old lines
        const removedLines = [];
        while (i < oldJson.length) {
          removedLines.push(oldJson[i++]);
        }
        if (removedLines.length > 0) {
          diff.push({ type: 'removed', lines: removedLines });
        }
        break;
      }
      
      if (oldJson[i] === newJson[j]) {
        // Lines match - collect unchanged lines
        const unchangedLines = [];
        while (i < oldJson.length && j < newJson.length && oldJson[i] === newJson[j]) {
          unchangedLines.push(oldJson[i]);
          i++;
          j++;
        }
        
        // Only show a few lines of context around changes
        if (diff.length > 0 && diff[diff.length - 1].type !== 'unchanged') {
          // Show up to 3 lines after a change
          diff.push({ type: 'unchanged', lines: unchangedLines.slice(0, 3) });
          if (unchangedLines.length > 6) {
            diff.push({ type: 'separator', lines: [`... ${unchangedLines.length - 6} unchanged lines ...`] });
            // Show up to 3 lines before next change
            if (i < oldJson.length || j < newJson.length) {
              diff.push({ type: 'unchanged', lines: unchangedLines.slice(-3) });
            }
          } else if (unchangedLines.length > 3) {
            diff.push({ type: 'unchanged', lines: unchangedLines.slice(-Math.min(3, unchangedLines.length - 3)) });
          }
        } else if (i < oldJson.length || j < newJson.length) {
          // Show context before first change or between changes
          if (unchangedLines.length > 3) {
            diff.push({ type: 'unchanged', lines: unchangedLines.slice(-3) });
          } else {
            diff.push({ type: 'unchanged', lines: unchangedLines });
          }
        }
      } else {
        // Lines differ - find the extent of the change
        const removedLines = [];
        const addedLines = [];
        
        // Collect changed lines by looking for the next matching line
        let oldEnd = i;
        let newEnd = j;
        let foundMatch = false;
        
        // Look ahead to find where lines sync up again
        for (let lookAhead = 1; lookAhead < 20 && !foundMatch; lookAhead++) {
          for (let oi = i; oi <= Math.min(i + lookAhead, oldJson.length - 1); oi++) {
            for (let ni = j; ni <= Math.min(j + lookAhead, newJson.length - 1); ni++) {
              if (oldJson[oi] === newJson[ni] && oldJson[oi].trim() !== '' && oldJson[oi].trim() !== '}' && oldJson[oi].trim() !== '},') {
                oldEnd = oi;
                newEnd = ni;
                foundMatch = true;
                break;
              }
            }
            if (foundMatch) break;
          }
        }
        
        // If no match found, take a reasonable chunk
        if (!foundMatch) {
          oldEnd = Math.min(i + 10, oldJson.length);
          newEnd = Math.min(j + 10, newJson.length);
        }
        
        // Collect the different lines
        while (i < oldEnd) {
          removedLines.push(oldJson[i++]);
        }
        while (j < newEnd) {
          addedLines.push(newJson[j++]);
        }
        
        if (removedLines.length > 0) {
          diff.push({ type: 'removed', lines: removedLines });
        }
        if (addedLines.length > 0) {
          diff.push({ type: 'added', lines: addedLines });
        }
      }
    }
    
    return diff;
  };

  useEffect(() => {
    // Scroll to selected message when it changes
    if (selectedMessageId && messageRefs.current[selectedMessageId]) {
      messageRefs.current[selectedMessageId].scrollIntoView({ 
        behavior: 'smooth', 
        block: 'center' 
      });
    }
  }, [selectedMessageId]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const getIntentForMessage = (message) => {
    return intents.find(intent => intent.id === message.intent_id);
  };

  const shouldShowIntentTransition = (message, index) => {
    if (index === 0) return true;
    
    const prevMessage = messages[index - 1];
    return message.intent_id !== prevMessage.intent_id;
  };

  const formatTimestamp = (timestamp) => {
    const date = new Date(timestamp);
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  const getCartState = (message) => {
    const payload = parseRawPayload(message?.raw_payload);
    if (!payload) return null;
    return payload.cart || payload.cartState || null;
  };

  const getPreviousCartState = (messages, currentIndex) => {
    for (let i = currentIndex - 1; i >= 0; i--) {
      const prevMessage = messages[i];
      if (prevMessage.direction === 'bot') {
        const cartState = getCartState(prevMessage);
        if (cartState) return cartState;
      }
    }
    return null;
  };

  const shouldShowCartDiff = (message, index) => {
    if (message.direction !== 'bot') return false;
    
    const currentCart = getCartState(message);
    if (!currentCart) return false;
    
    const previousCart = getPreviousCartState(messages, index);
    
    // Debug logging
    if (message.body && message.body.includes('Large size')) {
      console.log('Debug: Message with size change:', message);
      console.log('Debug: Current cart:', currentCart);
      console.log('Debug: Previous cart:', previousCart);
    }
    
    // Check for any differences in items count, combos count, or total
    const currentItemsCount = (currentCart.items || []).length;
    const previousItemsCount = (previousCart?.items || []).length;
    const currentCombosCount = (currentCart.combos || []).length;
    const previousCombosCount = (previousCart?.combos || []).length;
    const currentTotal = currentCart.total || 0;
    const previousTotal = previousCart?.total || 0;
    
    if (currentItemsCount !== previousItemsCount || 
        currentCombosCount !== previousCombosCount ||
        currentTotal !== previousTotal) {
      return true;
    }
    
    // Check for changes in item details (quantity, addons)
    const currentItems = currentCart.items || [];
    const previousItems = previousCart?.items || [];
    
    // Create maps for easier comparison
    const prevItemsMap = new Map();
    previousItems.forEach(item => {
      const key = `${item.name}-${item.option || 'default'}`;
      prevItemsMap.set(key, item);
    });
    
    // Also create a map by name only to detect option changes
    const prevItemsByName = new Map();
    previousItems.forEach(item => {
      if (!prevItemsByName.has(item.name)) {
        prevItemsByName.set(item.name, []);
      }
      prevItemsByName.get(item.name).push(item);
    });
    
    const currItemsByName = new Map();
    currentItems.forEach(item => {
      if (!currItemsByName.has(item.name)) {
        currItemsByName.set(item.name, []);
      }
      currItemsByName.get(item.name).push(item);
    });
    
    // Check for option changes on items with the same name
    for (const [itemName, prevItems] of prevItemsByName) {
      const currItems = currItemsByName.get(itemName) || [];
      if (prevItems.length === 1 && currItems.length === 1) {
        // Same item, check if option changed
        if (prevItems[0].option !== currItems[0].option) {
          return true; // Option changed
        }
      }
    }
    
    // Check each current item for changes
    for (const currentItem of currentItems) {
      const key = `${currentItem.name}-${currentItem.option || 'default'}`;
      const prevItem = prevItemsMap.get(key);
      
      if (!prevItem) continue; // New item or option change, already handled above
      
      // Check quantity changes
      if (currentItem.quantity !== prevItem.quantity) return true;
      
      // Check addon changes
      const currentAddons = new Set(currentItem.addons || []);
      const prevAddons = new Set(prevItem.addons || []);
      
      // Check if addon counts differ
      if (currentAddons.size !== prevAddons.size) return true;
      
      // Check if any addons are different
      for (const addon of currentAddons) {
        const addonStr = typeof addon === 'string' ? addon : (addon.name || JSON.stringify(addon));
        const found = [...prevAddons].some(prevAddon => {
          const prevAddonStr = typeof prevAddon === 'string' ? prevAddon : (prevAddon.name || JSON.stringify(prevAddon));
          return addonStr === prevAddonStr;
        });
        if (!found) return true;
      }
    }
    
    // Check for changes in combo details
    const currentCombos = currentCart.combos || [];
    const previousCombos = previousCart?.combos || [];
    
    // Create maps for combo comparison
    const prevCombosMap = new Map();
    previousCombos.forEach(combo => {
      prevCombosMap.set(combo.name, combo);
    });
    
    // Check each current combo for changes
    for (const currentCombo of currentCombos) {
      const prevCombo = prevCombosMap.get(currentCombo.name);
      
      if (!prevCombo) continue; // New combo, already caught by count check
      
      // Check quantity changes
      if (currentCombo.quantity !== prevCombo.quantity) return true;
      
      // Check combo items changes
      const currentComboItems = currentCombo.items || [];
      const prevComboItems = prevCombo.items || [];
      
      if (currentComboItems.length !== prevComboItems.length) return true;
      
      // Check if combo items have changed
      for (let i = 0; i < currentComboItems.length; i++) {
        const currentItem = currentComboItems[i];
        const prevItem = prevComboItems[i];
        
        if (!prevItem || 
            currentItem.name !== prevItem.name ||
            currentItem.option !== prevItem.option ||
            JSON.stringify(currentItem.addons) !== JSON.stringify(prevItem.addons)) {
          return true;
        }
      }
    }
    
    return false;
  };

  return (
    <div className="flex-1 bg-gray-900 shadow-xl flex flex-col">
      <div 
        ref={containerRef}
        className="flex-1 overflow-y-auto p-6 overscroll-contain"
        style={{ overscrollBehavior: 'contain' }}
      >
        {messages.length === 0 && conversationStatus === 'pending' && (
          <div className="flex items-center justify-center h-full">
            <div className="text-gray-400">Waiting for conversation to start...</div>
          </div>
        )}

	        {messages.map((message, index) => {
	          const intent = getIntentForMessage(message);
	          const showTransition = shouldShowIntentTransition(message, index);
	          const toolSummary = message.direction === 'bot' ? getToolSummary(message) : null;

	          return (
	          <div key={message.id}>
              {showTransition && intent && (
                <div className="relative my-8">
                  {/* Gradient lines */}
                  <div className="absolute inset-x-0 top-1/2 -translate-y-1/2 flex items-center">
                    <div className="flex-1 h-px bg-gradient-to-r from-transparent via-cyan-500/30 to-transparent"></div>
                  </div>
                  
                  {/* Downward chevrons */}
                  <div className="absolute inset-x-0 top-full -mt-3 flex justify-center space-x-1">
                    <svg className="w-3 h-3 text-cyan-500/20 animate-pulse" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
                    </svg>
                    <svg className="w-3 h-3 text-cyan-500/30 animate-pulse animation-delay-150" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
                    </svg>
                    <svg className="w-3 h-3 text-cyan-500/20 animate-pulse animation-delay-300" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
                    </svg>
                  </div>
                  
                  {/* Center badge */}
                  <div className="relative flex justify-center">
                    <div className="relative bg-slate-900 px-6 py-3 rounded-full border border-cyan-500/50 shadow-lg shadow-cyan-500/20">
                      {/* Inner glow */}
                      <div className="absolute inset-0 bg-gradient-to-r from-cyan-500/10 to-blue-500/10 rounded-full blur-sm"></div>
                      
                      {/* Content */}
                      <div className="relative flex items-center space-x-3">
                        {/* Intent number */}
                        <div className="flex items-center space-x-2">
                          <span className="text-xs font-mono text-cyan-400 bg-cyan-900/30 px-2 py-0.5 rounded border border-cyan-800/50">
                            {String(intents.indexOf(intent) + 1).padStart(3, '0')}
                          </span>
                        </div>
                        
                        {/* Intent type icon */}
                        <div className="relative">
                          <div className="absolute inset-0 bg-cyan-500 blur-md opacity-50"></div>
                          <svg className="relative w-4 h-4 text-cyan-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            {intent.intent_type === 'message' && (
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                            )}
                            {intent.intent_type === 'inquiry' && (
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                            )}
                            {intent.intent_type.startsWith('cart_') && (
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z" />
                            )}
                          </svg>
                        </div>
                        
                        {/* Intent type label */}
                        <div className="flex flex-col items-center">
                          <span className="text-[10px] font-mono text-cyan-400/70 uppercase tracking-widest">
                            Starting
                          </span>
                          <span className="text-xs font-bold text-white uppercase tracking-wider -mt-0.5">
                            {intent.intent_type.replace(/_/g, ' ')}
                          </span>
                        </div>
                        
                        {/* Arrow indicator */}
                        <div className="flex items-center">
                          <svg className="w-5 h-5 text-cyan-400 animate-bounce" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 14l-7 7m0 0l-7-7m7 7V3" />
                          </svg>
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  {/* Tech grid pattern */}
                  <div className="absolute inset-x-0 top-1/2 -translate-y-1/2 h-8 opacity-5" style={{
                    backgroundImage: `linear-gradient(to right, rgb(6 182 212 / 0.3) 1px, transparent 1px), linear-gradient(to bottom, rgb(6 182 212 / 0.3) 1px, transparent 1px)`,
                    backgroundSize: '20px 20px',
                    maskImage: 'linear-gradient(to right, transparent, black 20%, black 80%, transparent)'
                  }}></div>
                </div>
              )}

              {shouldShowCartDiff(message, index) && (
                <div className="mb-2 flex justify-start">
                  <div className="max-w-2xl w-full">
                    <div 
                      className="cursor-pointer"
                      onClick={() => setExpandedCartDiff(expandedCartDiff === message.id ? null : message.id)}
                    >
                      <JsonDiff 
                        oldData={getPreviousCartState(messages, index)}
                        newData={getCartState(message)}
                        isExpanded={expandedCartDiff === message.id}
                      />
                    </div>
                    
                    {expandedCartDiff === message.id && (
                      <div className="mt-4 bg-gray-950 rounded-lg p-4 border border-gray-800">
                        <h4 className="text-sm font-semibold text-gray-300 mb-3">Raw JSON Diff</h4>
                        <div className="grid grid-cols-1 gap-4">
                          {/* Diff View */}
                          <div>
                            <h5 className="text-xs font-medium text-gray-400 mb-2">Changes</h5>
                            <div className="bg-black p-3 rounded text-xs overflow-x-auto max-h-96 overflow-y-auto font-mono">
                              {generateJsonDiff(
                                getPreviousCartState(messages, index) || {},
                                getCartState(message) || {}
                              ).map((group, groupIdx) => (
                                <div key={groupIdx} className="mb-1">
                                  {group.type === 'separator' ? (
                                    <div className="text-gray-600 text-center py-1">
                                      {group.lines[0]}
                                    </div>
                                  ) : (
                                    <div className={`${
                                      group.type === 'added' 
                                        ? 'bg-green-900/20 border-l-4 border-green-500' 
                                        : group.type === 'removed' 
                                        ? 'bg-red-900/20 border-l-4 border-red-500' 
                                        : ''
                                    }`}>
                                      {group.lines.map((line, lineIdx) => (
                                        <div
                                          key={lineIdx}
                                          className={`pl-2 ${
                                            group.type === 'added' 
                                              ? 'text-green-400' 
                                              : group.type === 'removed' 
                                              ? 'text-red-400' 
                                              : 'text-gray-500'
                                          }`}
                                        >
                                          <span className="select-none opacity-50 inline-block w-4">
                                            {group.type === 'added' ? '+' : group.type === 'removed' ? '-' : ' '}
                                          </span>
                                          <span className="ml-2">{line}</span>
                                        </div>
                                      ))}
                                    </div>
                                  )}
                                </div>
                              ))}
                            </div>
                          </div>
                          
                          {/* Side by side view */}
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                              <h5 className="text-xs font-medium text-gray-400 mb-2">Previous Cart</h5>
                              <pre className="bg-black text-gray-400 p-3 rounded text-xs overflow-x-auto max-h-64 overflow-y-auto">
{JSON.stringify(getPreviousCartState(messages, index) || {}, null, 2)}
                              </pre>
                            </div>
                            <div>
                              <h5 className="text-xs font-medium text-gray-400 mb-2">Current Cart</h5>
                              <pre className="bg-black text-gray-400 p-3 rounded text-xs overflow-x-auto max-h-64 overflow-y-auto">
{JSON.stringify(getCartState(message) || {}, null, 2)}
                              </pre>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}

	              <div
	                ref={el => messageRefs.current[message.id] = el}
	                className={`mb-4 flex ${
	                  message.direction === 'user' ? 'justify-end' : 'justify-start'
	                }`}
	              >
	                <div
	                  className={`max-w-2xl ${
	                    message.direction === 'user'
	                      ? selectedMessageId === message.id 
	                        ? 'bg-blue-700 text-white ring-2 ring-blue-400 ring-offset-2'
                        : 'bg-blue-600 text-white'
                      : selectedMessageId === message.id 
                        ? 'bg-gray-800 text-gray-100 ring-2 ring-gray-500 ring-offset-2 ring-offset-gray-900'
                        : 'bg-gray-800 text-gray-100 border border-gray-700'
                  } rounded-lg px-4 py-3 cursor-pointer hover:opacity-90 transition-all`}
                  onClick={() => onMessageClick(message)}
                >
                  <div className="text-sm whitespace-pre-wrap">
                    {message.body}
                  </div>
                  
                  <div className={`text-xs mt-1 flex items-center gap-2 ${
                    message.direction === 'user' ? 'text-blue-200' : 'text-gray-400'
                  }`}>
                    <span>{formatTimestamp(message.created_at)}</span>
                    {message.direction === 'bot' && message.latency_ms > 0 && (
                      <span>({message.latency_ms}ms)</span>
                    )}
                    {message.direction === 'bot' && toolSummary && (
                      <>
                        <span>•</span>
                        <span
                          className="font-mono text-purple-300 bg-purple-900/30 px-2 py-0.5 rounded border border-purple-800/50 min-w-0 max-w-[420px] truncate"
                          title={`Tools (${toolSummary.count}): ${toolSummary.full}`}
                        >
                          Tools: {toolSummary.display}
                        </span>
                      </>
                    )}
                    {/* Validation score indicator for bot messages */}
                    {message.direction === 'bot' && message.validation_score !== null && message.validation_score !== undefined && (
                      <>
                        <span>•</span>
                        <span className={`font-bold px-2 py-0.5 rounded ${
                          message.validation_passed
                            ? 'bg-green-900/50 text-green-400 border border-green-800'
                            : 'bg-red-900/50 text-red-400 border border-red-800'
                        }`}>
                          {message.validation_score.toFixed(2)} {message.validation_passed ? '✓' : '✗'}
                        </span>
                      </>
	                    )}
	                  </div>
	                </div>
	              </div>
            </div>
          );
        })}

        {conversationStatus === 'running' && (
          <div className="flex items-center justify-center py-4">
            <div className="flex space-x-2">
              <div className="w-2 h-2 bg-gray-500 rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></div>
              <div className="w-2 h-2 bg-gray-500 rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></div>
              <div className="w-2 h-2 bg-gray-500 rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></div>
            </div>
          </div>
        )}

        <div ref={messagesEndRef} />
      </div>
    </div>
  );
};

export default MessagesContainer;
